<template>
  <div class="flex flex-col">
    <h1 class="text-3xl text-gray-800 font-semibold mb-6">
      {{ t('EMAIL_VERIFICATION.TITLE') }}
    </h1>

    <div class="description mb-6">
      <p class="text-lg text-gray-600 mb-2">{{ t('EMAIL_VERIFICATION.DESCRIPTION') }} {{ props.email }}</p>
      <p class="text-lg text-gray-600 mb-2">
        {{ t('EMAIL_VERIFICATION.ENTER_CODE') }}
      </p>
      <p class="text-lg font-semibold text-gray-800">
        {{ t('EMAIL_VERIFICATION.VERIFICATION_CODE') }}
      </p>
    </div>

    <!-- 6-digit OTP inputs (following bringg-frontend structure) -->
    <div
      class="flex gap-4 mb-4"
      role="group"
      :aria-label="t('EMAIL_VERIFICATION.VERIFICATION_CODE')"
      aria-describedby="otp-instructions otp-error">
      <div
        v-for="(key, index) in Object.keys(code)"
        :key="key"
        class="rounded-md border border-gray-300 flex w-12 py-2 flex-col justify-center items-center h-14"
        :class="{ 'border-red-400': otpVerificationFailed }">
        <input
          :id="key"
          :ref="
            (el) => {
              inputRefs[key] = el;
            }
          "
          v-model="code[key]"
          type="text"
          maxlength="1"
          autocomplete="off"
          inputmode="numeric"
          pattern="[0-9]"
          class="border-0 text-center w-full bg-transparent h-full text-3xl outline-none focus:outline-none"
          :disabled="disabled"
          :aria-label="`${t('EMAIL_VERIFICATION.DIGIT_LABEL')} ${index + 1} ${t('EMAIL_VERIFICATION.OF_SIX')}`"
          :aria-describedby="otpVerificationFailed ? 'otp-error' : 'otp-instructions'"
          :aria-invalid="otpVerificationFailed"
          @input="handleEvent(key)"
          @keydown="handlePressedKey"
          @click="handleFocus(key)"
          @focus="handleFocus(key)" />
      </div>
    </div>

    <!-- Screen reader instructions -->
    <div
      id="otp-instructions"
      class="sr-only">
      {{ t('EMAIL_VERIFICATION.SCREEN_READER_INSTRUCTIONS') }}
    </div>

    <!-- Error message -->
    <div
      v-if="otpVerificationFailed"
      id="otp-error"
      class="text-red-500 text-sm mb-4"
      role="alert"
      :aria-live="otpVerificationFailed ? 'assertive' : 'off'">
      {{ t('EMAIL_VERIFICATION.INCORRECT_CODE') }}
    </div>

    <!-- Expiration/timer message -->
    <p
      v-if="!minutesToExpiration"
      class="my-2.5 text-gray-600 mb-4"
      role="status"
      aria-live="polite">
      {{ t('EMAIL_VERIFICATION.CODE_IS_EXPIRED') }}
    </p>
    <p
      v-else
      class="my-2.5 text-gray-600 mb-4"
      role="status"
      aria-live="polite">
      {{ t('EMAIL_VERIFICATION.EXPIRATION_TIME') }} {{ formatCountdown(secondsToExpiration) }}
    </p>

    <!-- Resend section -->
    <div class="flex flex-col mt-12">
      <!-- reCAPTCHA (shown after failed attempts) -->
      <div
        v-if="showCaptcha"
        class="mb-4">
        <vue-recaptcha
          :sitekey="siteKey"
          @verify="setRecaptcha"
          @expire="cbExpiration" />
      </div>

      <!-- Show countdown message when resend is not available yet -->

      <div class="flex items-center text-lg">
        <span class="mr-2">
          {{ t('EMAIL_VERIFICATION.RESEND_EMAIL') }}
        </span>
        <button
          type="button"
          :class="[
            'bg-transparent border-0 cursor-pointer text-lg p-0 hover:underline',
            resendDisabled || secondsToExpiration > 285 || otpState.isResending
              ? 'text-gray-400 cursor-not-allowed'
              : 'text-blue-600'
          ]"
          :disabled="resendDisabled || secondsToExpiration > 285 || otpState.isResending"
          :aria-describedby="resendDisabled || secondsToExpiration > 285 ? 'resend-status' : null"
          @click="requestNewCode">
          <span v-if="otpState.isResending">
            {{ t('EMAIL_VERIFICATION.RESENDING') }}
          </span>
          <span v-else>
            {{ t('EMAIL_VERIFICATION.RESEND_CODE_BUTTON') }}
          </span>
        </button>
        <div
          v-if="resendDisabled || secondsToExpiration > 0"
          id="resend-status"
          class="sr-only">
          {{
            secondsToExpiration > 0
              ? t('EMAIL_VERIFICATION.RESEND_DISABLED_TIMER') + ' ' + formatCountdown(secondsToExpiration)
              : t('EMAIL_VERIFICATION.RESEND_IN_PROGRESS')
          }}
        </div>
      </div>

      <!-- Success message when code is resent -->
      <div
        v-if="resendSuccess"
        class="mt-4 p-3 bg-green-50 border border-green-200 rounded-md"
        role="status"
        aria-live="polite">
        <div class="flex">
          <div class="flex-shrink-0">
            <svg
              class="h-5 w-5 text-green-400"
              fill="currentColor"
              viewBox="0 0 20 20">
              <path
                fill-rule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clip-rule="evenodd" />
            </svg>
          </div>
          <div class="ml-3">
            <p class="text-sm font-medium text-green-800">
              {{ t('EMAIL_VERIFICATION.CODE_RESENT_SUCCESS') }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TwoFactorAuth'
};
</script>

<script setup>
import { useTranslation } from 'i18next-vue';
import { computed, nextTick, onMounted, onUnmounted, reactive, ref } from 'vue';
import { VueRecaptcha } from 'vue-recaptcha';
import { useStore } from 'vuex';

import config from '../../config';

const { t } = useTranslation();
const store = useStore();

// Props
const props = defineProps({
  email: {
    type: String,
    required: true
  },
  expiration: {
    type: String,
    required: true
  }
});

// Vue refs for input elements (replacing direct DOM manipulation)
const inputRefs = ref({});

// Reactive state (following bringg-frontend structure)
const code = reactive({
  firstDigit: '',
  secondDigit: '',
  thirdDigit: '',
  fourthDigit: '',
  fifthDigit: '',
  sixthDigit: ''
});

const otpVerificationFailed = ref(false);
const resendDisabled = ref(false);
const resendSuccess = ref(false);
const showCaptcha = ref(false);
const recaptcha = ref(null);
const minutesToExpiration = ref(0);
const secondsToExpiration = ref(0);
const expirationInterval = ref(null);
// Rate limiting and validation state
const lastSubmitTime = ref(0);
const submitCount = ref(0);
const rateLimitResetTime = ref(0);

// Request deduplication
const requestQueue = new Map();

// Performance optimization: debounce input changes
const inputChangeTimeout = ref(null);

// Computed
const otpState = computed(() => store.getters['auth/otpState']);
const disabled = computed(() => otpState.value.isVerifying || minutesToExpiration.value <= 0);
const siteKey = config.get('CAPTCHA');

// Input sanitization helper
const sanitizeInput = (input) => {
  if (!input) {
    return '';
  }

  // Remove all non-numeric characters and limit to digits only
  const sanitized = input.replace(/[^0-9]/g, '');

  // Additional security: reject patterns that might be injection attempts
  if (sanitized.length > 10) {
    return ''; // Reject suspiciously long inputs
  }

  return sanitized;
};

// Methods (replicating bringg-frontend logic)
const handleEvent = (digitKey) => {
  const codeKeys = Object.keys(code);
  const inputIndex = codeKeys.indexOf(digitKey);
  let inputValue = code[digitKey];

  // Enhanced input sanitization
  inputValue = sanitizeInput(inputValue);

  if (!inputValue) {
    code[digitKey] = '';

    return;
  }

  // Handle paste of code with enhanced sanitization
  if (inputValue?.length >= 6) {
    const lastSixDigits = inputValue.slice(-6).split('');

    // Sanitize each digit before setting
    lastSixDigits.forEach((digit, index) => {
      if (index < codeKeys.length) {
        const sanitizedDigit = sanitizeInput(digit);

        code[codeKeys[index]] = sanitizedDigit.slice(0, 1); // Ensure only 1 character
      }
    });
  }

  // Move focus to next input if single digit entered
  if (inputValue?.length === 1 && inputIndex >= 0 && inputIndex < codeKeys.length - 1) {
    focusNext(codeKeys[inputIndex + 1]);
  }

  // if number was pasted to the input which already has value
  if (inputValue?.length > 1 && inputValue?.length < 6) {
    const lastDigit = sanitizeInput(inputValue.slice(-1)).slice(0, 1);

    code[digitKey] = lastDigit; // Keep only the last sanitized digit
    if (inputIndex < codeKeys.length - 1) {
      focusNext(codeKeys[inputIndex + 1]);
    }
  } else if (inputValue?.length === 1) {
    // Ensure single digit is also sanitized
    code[digitKey] = sanitizeInput(inputValue).slice(0, 1);
  }

  const completeCode = [
    code.firstDigit,
    code.secondDigit,
    code.thirdDigit,
    code.fourthDigit,
    code.fifthDigit,
    code.sixthDigit
  ].join('');

  if (completeCode.length === 6) {
    // Debounce code submission to prevent rapid-fire submissions
    if (inputChangeTimeout.value) {
      clearTimeout(inputChangeTimeout.value);
    }

    inputChangeTimeout.value = setTimeout(() => {
      sendCode(completeCode);
      inputRefs.value[digitKey]?.blur();
    }, 100); // 100ms debounce
  }
};

const handlePressedKey = (event) => {
  const codeObjKeys = Object.keys(code);
  const currentInputIndex = codeObjKeys.indexOf(event.target.id);

  if (
    currentInputIndex > 0 &&
    !code[event.target.id]?.length &&
    (event.key === 'Backspace' || event.key === 'Delete')
  ) {
    nextTick(() => {
      inputRefs.value[codeObjKeys[currentInputIndex - 1]]?.focus();
    });
  }
};

const handleFocus = (digitKey) => {
  nextTick(() => {
    const element = inputRefs.value[digitKey];

    if (element) {
      element.setSelectionRange(0, 1);
    }
  });
};

const focusNext = (nextKey) => {
  nextTick(() => {
    const nextElement = inputRefs.value[nextKey];

    if (nextElement) {
      nextElement.focus();
    }
  });
};

// Request deduplication helper
const deduplicateRequest = async (requestKey, requestFn) => {
  // Check if same request is already in progress
  if (requestQueue.has(requestKey)) {
    return requestQueue.get(requestKey);
  }

  // Create and store the request promise
  const requestPromise = requestFn().finally(() => {
    // Clean up after request completes
    requestQueue.delete(requestKey);
  });

  requestQueue.set(requestKey, requestPromise);

  return requestPromise;
};

const sendCode = async (otpCode) => {
  otpVerificationFailed.value = false;

  // Rate limiting check
  const now = Date.now();
  const minInterval = config.get('OTP_CONFIG')?.RETRY_DELAY || 1000;

  if (now - lastSubmitTime.value < minInterval) {
    return; // Rate limited
  }

  // Reset submit count every minute
  if (now > rateLimitResetTime.value) {
    submitCount.value = 0;
    rateLimitResetTime.value = now + 60000;
  }

  submitCount.value++;
  lastSubmitTime.value = now;

  // Enhanced validation with additional security checks
  const sanitizedOtp = sanitizeInput(otpCode);

  // Validate format and check for suspicious patterns
  if (!/^\d{6}$/.test(sanitizedOtp)) {
    otpVerificationFailed.value = true;

    return;
  }

  // Additional security: reject common weak patterns
  const suspiciousPatterns = [
    /000000/, // All zeros
    /111111/, // All ones
    /123456/, // Sequential
    /654321/, // Reverse sequential
    /(\d)\1{5}/ // All same digit
  ];

  const hasSuspiciousPattern = suspiciousPatterns.some((pattern) => pattern.test(sanitizedOtp));

  if (hasSuspiciousPattern) {
    // Don't auto-reject suspicious patterns, but log for monitoring
    // SECURITY: Don't log actual OTP values
    // eslint-disable-next-line no-console
    console.warn('Suspicious OTP pattern detected: potential security issue');
  }

  // Use sanitized OTP for submission
  otpCode = sanitizedOtp;

  // Deduplicate request by OTP code
  const requestKey = `verify_${otpCode}`;

  try {
    const result = await deduplicateRequest(requestKey, () =>
      store.dispatch('auth/loginWithOtp', {
        otpCode,
        recaptchaToken: recaptcha.value
      })
    );

    if (!result.success) {
      otpVerificationFailed.value = true;

      if (result.showCaptcha) {
        showCaptcha.value = true;
      }

      // Reset inputs on failure
      Object.keys(code).forEach((key) => {
        code[key] = '';
      });

      // Focus first input
      nextTick(() => {
        inputRefs.value.firstDigit?.focus();
      });
    }
  } catch (error) {
    otpVerificationFailed.value = true;

    // Reset inputs on error
    Object.keys(code).forEach((key) => {
      code[key] = '';
    });

    nextTick(() => {
      inputRefs.value.firstDigit?.focus();
    });
  }
};

const requestNewCode = async () => {
  resendDisabled.value = true;
  resendSuccess.value = false;

  // Deduplicate resend requests
  const requestKey = 'resend_otp';

  try {
    const result = await deduplicateRequest(requestKey, () => store.dispatch('auth/sendOtpEmail'));

    if (result.success) {
      // Reset code inputs and verification state
      Object.keys(code).forEach((key) => {
        code[key] = '';
      });
      otpVerificationFailed.value = false;
      resendSuccess.value = true;

      // Restart the expiration timer if we got a new expiration time
      if (result.expiration) {
        // Stop existing timer and restart with new expiration
        stopExpirationInterval();
        startExpirationTimeInterval(result.expiration);
      }

      // Hide success message after 3 seconds
      setTimeout(() => {
        resendSuccess.value = false;
      }, 3000);

      nextTick(() => {
        inputRefs.value.firstDigit?.focus();
      });
    }
  } catch (error) {
    // Failed to resend OTP - error handled by action
  } finally {
    resendDisabled.value = false;
  }
};

const setRecaptcha = (recaptchaValue) => {
  recaptcha.value = recaptchaValue;
  const completeCode = [
    code.firstDigit,
    code.secondDigit,
    code.thirdDigit,
    code.fourthDigit,
    code.fifthDigit,
    code.sixthDigit
  ].join('');

  if (completeCode.length === 6) {
    sendCode(completeCode);
  }
};

const cbExpiration = () => {
  recaptcha.value = null;
};

// Format countdown as MM:SS
const formatCountdown = (totalSeconds) => {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

// Optimized timer functions with better performance
const getExpirationMinutes = (expirationTime) => {
  if (!expirationTime) {
    return 0;
  }

  const expirationDate = new Date(expirationTime);
  const now = new Date();

  if (isNaN(expirationDate.getTime())) {
    return 0;
  }

  const diffMs = expirationDate.getTime() - now.getTime();
  const minutes = Math.ceil(diffMs / (1000 * 60)); // Use ceil to show remaining time properly
  const result = Math.max(0, minutes);

  return result;
};

const getExpirationSeconds = (expirationTime) => {
  if (!expirationTime) {
    return 0;
  }

  const expirationDate = new Date(expirationTime);
  const now = new Date();

  if (isNaN(expirationDate.getTime())) {
    return 0;
  }

  const diffMs = expirationDate.getTime() - now.getTime();
  const seconds = Math.floor(diffMs / 1000);
  const result = Math.max(0, seconds);

  return result;
};

const startExpirationTimeInterval = (expirationTime) => {
  // Update both minutes and seconds
  const updateTimer = () => {
    minutesToExpiration.value = getExpirationMinutes(expirationTime);
    secondsToExpiration.value = getExpirationSeconds(expirationTime);

    if (secondsToExpiration.value <= 0) {
      stopExpirationInterval();
      cbExpiration();
    }
  };

  // Update immediately
  updateTimer();

  // Then update every second for accurate countdown
  expirationInterval.value = setInterval(updateTimer, 1000);
};

const stopExpirationInterval = () => {
  if (expirationInterval.value) {
    clearInterval(expirationInterval.value);
    expirationInterval.value = null;
  }
};

// Lifecycle
onMounted(() => {
  // Start expiration timer
  startExpirationTimeInterval(props.expiration);

  // Focus first input
  nextTick(() => {
    inputRefs.value.firstDigit?.focus();
  });
});

onUnmounted(() => {
  stopExpirationInterval();

  // Clean up debounce timeout to prevent memory leaks
  if (inputChangeTimeout.value) {
    clearTimeout(inputChangeTimeout.value);
    inputChangeTimeout.value = null;
  }
});
</script>

<style scoped>
/* Screen reader only - visually hidden but accessible to screen readers */
.sr-only {
  position: absolute !important;
  width: 1px !important;
  height: 1px !important;
  padding: 0 !important;
  margin: -1px !important;
  overflow: hidden !important;
  clip: rect(0, 0, 0, 0) !important;
  white-space: nowrap !important;
  border: 0 !important;
}

/* Focus indicators for better accessibility */
.otp-input:focus {
  outline: 2px solid #4f46e5;
  outline-offset: 2px;
  border-color: #4f46e5;
}

/* High contrast mode support */
@media (prefers-contrast: high) {
  .otp-input {
    border-width: 2px;
  }

  .otp-input:focus {
    outline-width: 3px;
  }

  .invalid .otp-input {
    border-color: #dc2626;
  }
}

/* Reduced motion support */
@media (prefers-reduced-motion: reduce) {
  .otp-input {
    transition: none !important;
  }
}
</style>
